import { makeAutoObservable } from 'mobx'
import { decorate, injectable, inject } from '@evertel/di'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'
import { Analytics as AnalyticsBase, EventArgs, FieldsObject } from '@evertel/analytics'
import { gtagAnalytics } from './gtagAnalytics'

class Analytics implements AnalyticsBase {
    platform = 'web'
    enabled = true

    constructor(
        private api: Api,
        private session: SessionService
    ) {
        makeAutoObservable(this)
    }

    async init() {
        if (!this.api.isProduction) {
            this.enabled = false
        }

        if (!this.enabled) return

        gtagAnalytics.init(this.session.currentUserId?.toString())
        return undefined
    }

    async logEvent(category: string, action: string, options?: { label?: string, value?: number, [i: string]: any }): Promise<any> {
        if (!action) {
            if (!this.api.isProduction) console.warn('ERROR LOGGING EVENT TO GA. Missing "action"')
            return
        }

        if (!this.enabled) return

        gtagAnalytics.event(category, action, options)
    }

    async logView(screen: string, isModal?: boolean, title?: string): Promise<any> {
        if (!screen) {
            if (!this.api.isProduction) console.warn('ERROR LOGGING PAGEVIEW IN GA. Missing "page"')
            return
        }

        if (!this.enabled) return

        if (isModal) {
            gtagAnalytics.modalView(screen)
        } else {
            gtagAnalytics.pageview(screen, title)
        }
    }

    async logException(description: string, fatal?: boolean): Promise<any> {
        if (!this.enabled) return
        gtagAnalytics.exception(description, fatal)
    }

    async logOutboundLink(link: string): Promise<void> {
        if (!this.enabled) return
        gtagAnalytics.outboundLink(link)
    }

    async setDimension(dataObject: FieldsObject): Promise<any> {
        if (!this.enabled) return
        gtagAnalytics.setUserProperties(dataObject)
    }

    async logLogin(): Promise<any> {
        if (!this.enabled) return
        await this.logEvent('App', 'login')
    }

    async logAppOpen(): Promise<any> {
        if (!this.enabled) return

        await this.logEvent('App', 'opened')
    }

    async logSignUp(): Promise<any> {
        if (!this.enabled) return

        await this.logEvent('App', 'signup')
    }
}

decorate(injectable(), Analytics)
decorate(inject(Api), Analytics, 0)
decorate(inject(SessionService), Analytics, 1)

export { Analytics }
